import { useState } from 'react'
import { CurrencyInput } from '@/components/CurrencyInput'
import { managerialIsoDate } from '@/lib/managerial-calendar'
import type { CreateOperatingCostMonthlyEntryInput } from '@/lib/api/operating-costs-admin.service'
import type { OperatingCostCategory } from '@/types/api'
import {
  MARKETING_CATEGORY_SLUG,
  formatCategoryTotal,
  OPERATING_COST_MONTH_OPTIONS,
  OPERATING_COST_YEAR_MIN,
  operatingCostYearMax,
  parseAmountInput,
  parseMonthInput,
  parseYearInput,
} from '../lib/operating-costs'
import styles from '../CustosOperacionaisPage.module.css'
import { AwsMonthlyEntryRow } from './AwsMonthlyEntryRow'

type AwsServerCostPanelProps = {
  category: OperatingCostCategory
  isSaving: boolean
  onCreateMonthlyEntry: (
    categoryId: number,
    input: CreateOperatingCostMonthlyEntryInput
  ) => Promise<boolean>
  onUpdateMonthlyEntry: (
    entryId: number,
    input: CreateOperatingCostMonthlyEntryInput
  ) => Promise<boolean>
  onDeleteMonthlyEntry: (entryId: number) => Promise<boolean>
}

function currentMonthValue(): string {
  return String(Number(managerialIsoDate().slice(5, 7)))
}

function currentYearValue(): string {
  return managerialIsoDate().slice(0, 4)
}

export function AwsServerCostPanel({
  category,
  isSaving,
  onCreateMonthlyEntry,
  onUpdateMonthlyEntry,
  onDeleteMonthlyEntry,
}: AwsServerCostPanelProps) {
  const yearMax = operatingCostYearMax()
  const [month, setMonth] = useState(currentMonthValue)
  const [year, setYear] = useState(currentYearValue)
  const [amount, setAmount] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const entries = category.monthly_entries
  const isMarketing = category.slug === MARKETING_CATEGORY_SLUG

  function resetForm() {
    setMonth(currentMonthValue())
    setYear(currentYearValue())
    setAmount('')
    setFormError(null)
  }

  return (
    <article
      className={styles.categoryCard}
      aria-labelledby={`operating-cost-category-${category.id}`}
    >
      <header className={styles.categoryHeader}>
        <div className={styles.categoryHeading}>
          <h3
            id={`operating-cost-category-${category.id}`}
            className={styles.categoryTitle}
          >
            {category.name}
          </h3>
          <span className={styles.badge}>Padrão</span>
        </div>
        <p className={styles.categoryTotal}>{formatCategoryTotal(category)}</p>
      </header>

      <p className={styles.othersCopy}>
        {isMarketing
          ? 'Registre o investimento em marketing e aquisição por mês. Esses valores alimentam o CAC e as despesas do dashboard executivo. Sem registro no mês, o CAC fica indisponível. Para informar ausência de gasto, registre explicitamente o valor zero.'
          : 'Lance o custo do servidor AWS por mês e ano, em reais. Dá para registrar mais de um valor no mesmo mês, inclusive meses passados ou futuros, e depois editar ou excluir cada lançamento.'}
      </p>

      {entries.length === 0 ? (
        <p className={styles.empty}>Nenhum lançamento mensal nesta categoria.</p>
      ) : (
        <ul className={styles.entryList}>
          {entries.map((entry) => (
            <li key={entry.id}>
              <AwsMonthlyEntryRow
                entry={entry}
                isSaving={isSaving}
                onUpdate={onUpdateMonthlyEntry}
                onDelete={onDeleteMonthlyEntry}
              />
            </li>
          ))}
        </ul>
      )}

      <form
        className={styles.monthlyForm}
        onSubmit={(event) => {
          event.preventDefault()
          const parsedMonth = parseMonthInput(month)
          const parsedYear = parseYearInput(year)
          const parsedAmount = parseAmountInput(amount)
          if (parsedMonth === null) {
            setFormError('Informe um mês entre janeiro e dezembro.')
            return
          }
          if (parsedYear === null) {
            setFormError(`Informe um ano entre ${OPERATING_COST_YEAR_MIN} e ${yearMax}.`)
            return
          }
          if (parsedAmount === null) {
            setFormError('Informe o valor em reais.')
            return
          }
          setFormError(null)
          void onCreateMonthlyEntry(category.id, {
            month: parsedMonth,
            year: parsedYear,
            amount: parsedAmount,
          }).then((ok) => {
            if (ok) resetForm()
          })
        }}
      >
        <label className={styles.field} htmlFor={`new-aws-month-${category.id}`}>
          Mês
          <select
            id={`new-aws-month-${category.id}`}
            className={styles.input}
            value={month}
            disabled={isSaving}
            onChange={(event) => setMonth(event.target.value)}
          >
            {OPERATING_COST_MONTH_OPTIONS.map((option) => (
              <option key={option.value} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field} htmlFor={`new-aws-year-${category.id}`}>
          Ano
          <input
            id={`new-aws-year-${category.id}`}
            className={styles.input}
            inputMode="numeric"
            value={year}
            min={OPERATING_COST_YEAR_MIN}
            max={yearMax}
            maxLength={4}
            autoComplete="off"
            disabled={isSaving}
            onChange={(event) => setYear(event.target.value)}
          />
        </label>
        <label className={styles.field} htmlFor={`new-aws-amount-${category.id}`}>
          Valor
          <CurrencyInput
            id={`new-aws-amount-${category.id}`}
            value={amount}
            disabled={isSaving}
            onChange={setAmount}
          />
        </label>
        <button type="submit" className={styles.primaryButton} disabled={isSaving}>
          {isSaving ? 'Salvando...' : 'Adicionar'}
        </button>
      </form>
      {formError ? (
        <p className={styles.inlineError} role="alert">
          {formError}
        </p>
      ) : null}
    </article>
  )
}
